import React, { useEffect, useState } from 'react';
import { BaseBanner } from '../BaseBanner/BaseBanner';
import { Timer } from '@/components/common/Timer/Timer';
import styles from './LimitedTimeDiscountBanner.module.scss';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { getTargetDate } from '@/utils/helpers/limitedTimeDiscount';
import { calculateDiscount } from '@/utils/helpers/planUtils';
import { usePremium } from '@/hooks';
import {
  useAnalytics,
  EAnalyticsProvider,
} from '@/hooks/analytics/useAnalytics';
import { ANALYTICS } from '@/constants/analytics/analytics';
import { selectExtensionInfo } from '@/store/selectors';
import { RootState } from '@/store/store';
import { apiPremium } from '@/utils/api/apiPremium';
import { ECheckoutSource, IPlan } from '@/models/checkout.model';
import moment from 'moment';
import { featureFlag } from '@/utils';
import { GROWTH_BOOK } from '@/constants/features/feature-flag';
import { GTM_ECOMMERCE_EVENTS } from '@/utils/analytics/gtm-ecommerce';

interface ILimitedTimeDiscountBannerProps {
  onComplete: () => void;
  onVisibilityChange: (visible: boolean) => void;
}

export const LimitedTimeDiscountBanner: React.FC<
  ILimitedTimeDiscountBannerProps
> = ({ onComplete, onVisibilityChange }) => {
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [showBanner, setShowBanner] = useState(false);
  const extensionInfo = useSelector(selectExtensionInfo);
  const user = useSelector((state: RootState) => state.user);
  const premiumHook = usePremium();
  const { fireAnalyticsEvent } = useAnalytics();

  const isOneHourSinceInstall = (): boolean => {
    const installedTime = extensionInfo.installedTime;
    return moment().diff(moment(installedTime), 'hours') >= 1;
  };

  const getPurchasePlans = async (): Promise<void> => {
    const fetchedPlans = await apiPremium.getSubscriptionPlans(
      ECheckoutSource.limitedTimeDiscount
    );
    setPlans(fetchedPlans);
  };

  const onClaimDiscountClick = (): void => {
    fireAnalyticsEvent(ANALYTICS.EVENTS.CLAIM_DISCOUNT_BUTTON, {
      [ANALYTICS.PROPERTIES.EVENT_TYPE]: ANALYTICS.TYPES.CLICK,
      [ANALYTICS.PROPERTIES.SOURCE]: ANALYTICS.VALUES.HEADER,
      [ANALYTICS.PROPERTIES.IS_PREMIUM_HOOK]: true,
    });
    fireAnalyticsEvent(GTM_ECOMMERCE_EVENTS.ADD_TO_CART, { plan: plans[0] }, [
      EAnalyticsProvider.gtm,
    ]);
    premiumHook
      .openPremium({
        source: ANALYTICS.VALUES.LIMIT_TIME_DISCOUNT_HEADER,
        checkoutSource: ECheckoutSource.limitedTimeDiscount,
        plans,
      })
      .finally();
  };

  const shouldShowBanner = (): boolean => {
    const showBanner = featureFlag.getFeatureValue(
      GROWTH_BOOK.FEATURES.SHOW_LIMITED_TIME_DISCOUNT,
      true
    );
    if (!showBanner) {
      return false;
    }
    const hasTargetDate = getTargetDate() > 0;
    const isPremium = user.isPremium;
    return !isPremium && hasTargetDate && !isOneHourSinceInstall();
  };

  useEffect(() => {
    const initBanner = async (): Promise<void> => {
      if (shouldShowBanner()) {
        await getPurchasePlans();
      } else {
        setShowBanner(false);
        onVisibilityChange(false);
      }
    };

    initBanner();
  }, [user.isPremium]);

  useEffect(() => {
    if (plans?.length > 0) {
      setShowBanner(true);
      onVisibilityChange(true);
    } else {
      setShowBanner(false);
      onVisibilityChange(false);
    }
  }, [plans]);

  const handleTimerComplete = (): void => {
    setShowBanner(false);
    onComplete();
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <BaseBanner
        text={
          <Trans
            i18nKey="limitedTimeDiscount.bannerOfferTitle"
            values={{
              discount: calculateDiscount(plans[0]),
            }}
          />
        }
        button={{
          text: 'limitedTimeDiscount.bannerOfferBtnText',
          onClick: onClaimDiscountClick,
        }}
        icon="specialOfferDiscount.svg"
      >
        <Timer
          onComplete={handleTimerComplete}
          dateInMs={getTargetDate()}
          text="limitedTimeDiscount.timerText"
          small={true}
        />
      </BaseBanner>
    </div>
  );
};
